/* Copyright (c) 2020-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { Observable } from 'rxjs';
import { GamepadButtonName, MonitorOptions } from './gamepad.types';

export abstract class GamepadService {
    /**
     * Emits `true` if there is a connected gamepad, otherwise emits `false`
     */
    public abstract get isGamepadConnected$(): Observable<boolean>;

    /**
     * Emits `true` when any gamepad has its button pressed or joystick moved, otherwise emits `false`
     */
    public abstract get isAnyGamepadInUse$(): Observable<boolean>;

    /**
     * Emits `true` repeatedly when any gamepad's d-pad left button is pressed or left joystick is pushed to the left, otherwise emits `false`
     */
    public abstract get leftPressed$(): Observable<boolean>;

    /**
     * Emits `true` repeatedly when any gamepad's d-pad right button is pressed or left joystick is pushed to the right, otherwise emits `false`
     */
    public abstract get rightPressed$(): Observable<boolean>;

    /**
     * Emits `true` repeatedly when any gamepad's d-pad up button is pressed or left joystick is pushed forward, otherwise emits `false`
     */
    public abstract get upPressed$(): Observable<boolean>;

    /**
     * Emits `true` repeatedly when any gamepad's d-pad down button is pressed or left joystick is pushed backward, otherwise emits `false`
     */
    public abstract get downPressed$(): Observable<boolean>;

    /**
     * Emits `true` when any gamepad's right-stick is moved, otherwise emits `false`.
     */
    public abstract get rightStickMoved$(): Observable<boolean>;

    /**
     * Returns an observable reflecting the `pressed` property of a `GamepadButton`
     *
     * @param button the button to be observed
     * @param options the extra monitoring options
     * @returns an observable that emits `true` when the button is pressed on any gamepad, otherwise emits `false`.
     */
    public abstract onButtonPressed$(button: GamepadButtonName, options?: MonitorOptions): Observable<boolean>;

    /**
     * Emits `true` when a button is long-pressed on any gamepad
     */
    public abstract onButtonLongPressed$(button: GamepadButtonName, longPressDuration?: number): Observable<boolean>;

    /**
     * Emits `true` when a combination of buttons is pressed together on any gamepad, otherwise emits `false`
     */
    public abstract onButtonComboPressed$(buttons: GamepadButtonName[]): Observable<boolean>;
}
